function getShopifyRoute(action) {
  const root = window.Shopify?.routes?.root || "/";
  return `${root}${action.replace(/^\//, "")}`;
}

async function fetchJson(url, options) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }
  return response.json();
}

function runWhenReady(callback) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", callback, { once: true });
    return;
  }

  callback();
}

function toggleAddedDataset(element) {
  element.dataset.added = element.dataset.added === "true" ? "false" : "true";
}

if (!customElements.get("warranty-option-include")) {
  class WarrantyOptionInclude extends HTMLElement {
    constructor() {
      super();
      this.selectedOption = this.querySelector(".selected-option");
      this.warrantyButtons = [...this.querySelectorAll(".warranty-btn")];
      this.handleWarrantyChange = this.handleWarrantyChange.bind(this);
      this.handleAddonToggle = this.handleAddonToggle.bind(this);
      this.warrantiesBtnsLogic();
    }

    connectedCallback() {
      this.addEventListener("warranty::change", this.handleWarrantyChange);
    }

    disconnectedCallback() {
      this.removeEventListener("warranty::change", this.handleWarrantyChange);
      this.warrantyButtons.slice(1).forEach((button) => {
        button.removeEventListener("click", this.handleAddonToggle);
      });
    }

    handleWarrantyChange() {
      const selected = this.querySelector(".warranty-btn.selected");
      if (!selected || !this.selectedOption) {
        return;
      }

      const badge = selected.querySelector(".warranty-badge");
      const variantPrice = Number.parseFloat(
        selected.dataset.variantPrice || "",
      );
      const hasPrice = Number.isFinite(variantPrice);
      const formattedPrice = hasPrice ? variantPrice.toFixed(2) : null;
      const priceMarkup = hasPrice
        ? `+$<span class='addons-price' data-base-price="${formattedPrice}">${formattedPrice}</span>`
        : "Basic";

      this.selectedOption.classList.add("option-selected");
      this.selectedOption.innerHTML = badge
        ? `<span class="selected-option--badge">${badge.textContent}</span> ${priceMarkup}`
        : priceMarkup;
    }

    handleAddonToggle() {
      toggleAddedDataset(this);
    }

    warrantiesBtnsLogic() {
      const warranties = this.querySelectorAll("ul li");
      if (!warranties.length) return;

      const clickFirstWarranty = () => {
        this.warrantyButtons[0]?.click();
      };

      runWhenReady(clickFirstWarranty);

      warranties.forEach((warranty, index) => {
        if (index === 0) return;
        const warrantyBtn = warranty.querySelector(".warranty-btn");
        warrantyBtn?.addEventListener("click", this.handleAddonToggle);
      });

      const addons = new URLSearchParams(window.location.search).get(
        "product_addons",
      );
      if (addons) {
        warranties.forEach((warranty, index) => {
          if (index === 0) return;
          const warrantyBtn = warranty.querySelector(".warranty-btn");
          if (!warrantyBtn || !addons.includes(warrantyBtn.dataset.variantId)) {
            return;
          }
          this.dataset.added = "true";
        });
      }
    }
  }

  customElements.define("warranty-option-include", WarrantyOptionInclude);
}

if (!customElements.get("service-option")) {
  class ServiceOption extends HTMLElement {
    constructor() {
      super();
      this.selectedOption = this.querySelector(".selected-option");
      this.serviceInputs = [...this.querySelectorAll(".js-option input")];
      this.handleServiceChange = this.handleServiceChange.bind(this);
      this.handleAddonToggle = this.handleAddonToggle.bind(this);

      this.servicesBtnsLogic();
    }

    connectedCallback() {
      this.addEventListener("service::change", this.handleServiceChange);
    }

    disconnectedCallback() {
      this.removeEventListener("service::change", this.handleServiceChange);
      this.serviceInputs.slice(1).forEach((input) => {
        input.removeEventListener("click", this.handleAddonToggle);
      });
    }

    handleServiceChange() {
      this.selectedOption?.classList.add("option-selected");
    }

    handleAddonToggle() {
      toggleAddedDataset(this);
    }

    servicesBtnsLogic() {
      const serviceOptions = this.querySelectorAll(".js-option");
      if (!serviceOptions.length) return;

      const clickFirstService = () => {
        serviceOptions[0].click();
      };

      runWhenReady(clickFirstService);

      serviceOptions.forEach((warranty, index) => {
        if (index === 0) return;
        const warrantyBtn = warranty.querySelector("input");
        warrantyBtn?.addEventListener("click", this.handleAddonToggle);
      });

      const addons = new URLSearchParams(window.location.search).get(
        "product_addons",
      );
      if (addons) {
        serviceOptions.forEach((warranty, index) => {
          if (index === 0) return;
          const warrantyBtn = warranty.querySelector("input");
          if (!warrantyBtn || !addons.includes(warrantyBtn.dataset.variantId)) {
            return;
          }
          this.dataset.added = "true";
        });
      }
    }
  }

  customElements.define("service-option", ServiceOption);
}

if (!customElements.get("custom-slider-buttons")) {
  class CustomSliderButtons extends HTMLElement {
    constructor() {
      super();

      this.mediaGallery = this.closest("media-gallery");
      this.nextButton = this.querySelector(".csb--next");
      this.prevButton = this.querySelector(".csb--prev");
      this.onButtonClick = this.onButtonClick.bind(this);
      this.elements = this.getElements();
    }

    connectedCallback() {
      this.nextButton?.addEventListener("click", this.onButtonClick);
      this.prevButton?.addEventListener("click", this.onButtonClick);
    }

    disconnectedCallback() {
      this.nextButton?.removeEventListener("click", this.onButtonClick);
      this.prevButton?.removeEventListener("click", this.onButtonClick);
    }

    getElements() {
      return Array.from(
        this.mediaGallery?.querySelectorAll("li.thumbnail-list__item") || [],
      );
    }

    onButtonClick(event) {
      event.preventDefault();
      this.triggerSlide(event.currentTarget.name);
    }

    triggerSlide(name) {
      this.elements = this.getElements();

      const currentSlide = this.mediaGallery
        .querySelector(`button.thumbnail[aria-current="true"]`)
        .closest("li");
      const currentIndex = this.elements.indexOf(currentSlide);

      let element =
        name === "next"
          ? this.elements
              .slice(currentIndex)
              .find(
                (el) =>
                  !el.querySelector(`button.thumbnail[aria-current="true"]`) &&
                  !el.classList.contains("hide-image") &&
                  !el.classList.contains("custom-hidden"),
              )
          : this.elements
              .slice(0, currentIndex)
              .reverse()
              .find(
                (el) =>
                  !el.querySelector(`button.thumbnail[aria-current="true"]`) &&
                  !el.classList.contains("hide-image") &&
                  !el.classList.contains("custom-hidden"),
              );
      const isBundle = this.closest(".product-is-bundle");
      if (isBundle) {
        element =
          name === "next"
            ? this.elements
                .slice(currentIndex)
                .find(
                  (el) =>
                    !el.querySelector(`button.thumbnail[aria-current="true"]`),
                )
            : this.elements
                .slice(0, currentIndex)
                .reverse()
                .find(
                  (el) =>
                    !el.querySelector(`button.thumbnail[aria-current="true"]`),
                );
      }
      element
        ? element.querySelector("button").click()
        : console.error("Something went wrong");

      if (name === "prev" && !element && currentIndex === 1) {
        this.mediaGallery
          .querySelector(`li.thumbnail-list__item`)
          .querySelector("button")
          .click();
      }
    }
  }

  customElements.define("custom-slider-buttons", CustomSliderButtons);
}

if (!customElements.get("cart-addon-selector")) {
  class CartAddonSelector extends HTMLElement {
    static observedAttributes = ["selected-option"];
    cartContent = null;
    cartContentPromise = null;
    cartItemsParent = null;

    constructor() {
      super();
      this.key = this.getAttribute("key");
      this.cartItemsParent = this.closest("cart-items");
      this.animateInput = null;
      this.updatePromise = null;
      this.handleInputClick = this.handleInputClick.bind(this);
      this.handleInputChange = this.handleInputChange.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.handleInputClick);
      this.addEventListener("change", this.handleInputChange);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleInputClick);
      this.removeEventListener("change", this.handleInputChange);
    }

    getCartItemElement() {
      return this.closest(".cart-item");
    }

    getLineIndex() {
      return this.getCartItemElement()
        ?.getAttribute("id")
        ?.replace("CartItem-", "");
    }

    getParentItemFromDom() {
      const input = this.animateInput || this.querySelector("input");
      const parentId = Number.parseInt(input?.dataset.parentId || "", 10);
      if (!Number.isFinite(parentId)) {
        return null;
      }

      const quantityInput = this.getCartItemElement()?.querySelector(
        "input.quantity__input",
      );
      const quantity = Number.parseInt(quantityInput?.value || "", 10);

      return {
        id: parentId,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        properties: {
          _product_type: input?.dataset.productType || "",
        },
      };
    }

    handleInputClick(event) {
      const input = event.target?.closest?.("input");
      if (
        !input ||
        !this.contains(input) ||
        input.type !== "radio" ||
        this.getAttribute("selected-option") !== input.value
      ) {
        return;
      }

      event.preventDefault();
      this.animateInput = input;
      input.checked = false;
      this.setAttribute("selected-option", "");
    }

    handleInputChange(event) {
      const input = event.target?.closest?.("input");
      if (!input || !this.contains(input)) {
        return;
      }

      this.animateInput = input;
      if (input.type === "checkbox" && !input.checked) {
        this.setAttribute("selected-option", "");
      } else {
        this.setAttribute("selected-option", input.value);
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      switch (name) {
        case "selected-option": {
          const updatePromise = this.handleOptionChange(oldValue, newValue)
            .catch((error) => {
              console.error("Error updating cart addon:", error);
            })
            .finally(() => {
              if (this.updatePromise === updatePromise) {
                this.updatePromise = null;
              }
            });
          this.updatePromise = updatePromise;
          break;
        }
      }
    }

    async fetchCartData(forceRefresh = false) {
      if (!forceRefresh && this.cartContentPromise) {
        return this.cartContentPromise;
      }

      this.cartContentPromise = fetchJson(getShopifyRoute("cart.js"))
        .then((cart) => {
          this.cartContent = cart;
          return cart;
        })
        .catch(() => {
          throw new Error("ERROR! fetching data from the cart!!!");
        })
        .finally(() => {
          this.cartContentPromise = null;
        });

      return this.cartContentPromise;
    }

    getCartItemByKey() {
      const currentKey = this.getAttribute("key") || this.key;
      let result = this.cartContent.items.find(
        (cartItem) => cartItem.key === currentKey,
      );

      // Fallback: If Shopify regenerated the key hash, find the item by its row index and variant ID
      if (!result) {
        const lineId = this.getCartItemElement()?.getAttribute("id");
        if (lineId) {
          const lineIndex = parseInt(lineId.replace("CartItem-", ""), 10) - 1;
          const fallbackItem = this.cartContent.items[lineIndex];
          const expectedVariantId = currentKey.split(":")[0];
          if (
            fallbackItem &&
            (fallbackItem.variant_id.toString() === expectedVariantId ||
              fallbackItem.id.toString() === expectedVariantId)
          ) {
            result = fallbackItem;
          }
        }
      }

      // Ultimate fallback: Find the first matching variant ID
      if (!result) {
        const expectedVariantId = currentKey.split(":")[0];
        result = this.cartContent.items.find(
          (cartItem) =>
            cartItem.variant_id.toString() === expectedVariantId ||
            cartItem.id.toString() === expectedVariantId,
        );
      }

      if (!result) {
        throw new Error("ERROR! there is no item with key: " + currentKey);
      }

      // Keep the stored key up-to-date for future calls
      this.key = result.key;
      this.setAttribute("key", result.key);

      return result;
    }

    async removeCartItem(value, options = {}) {
      const parentItem = this.getCartItemByKey();
      const itemToRemove = this.cartContent.items.find(
        (cartItem) =>
          cartItem.id === Number(value) &&
          cartItem.properties &&
          cartItem.properties._for_product === parentItem.id.toString(),
      );
      if (!itemToRemove) {
        throw new Error(
          "ERROR! there is no item to remove with matching conditions " + value,
        );
      }
      const data = {
        id: itemToRemove.key,
        quantity: 0,
      };
      return this.cartAction(data, "cart/change.js", options);
    }

    async cartAction(formData, action, { renderSections = false } = {}) {
      const url = getShopifyRoute(action);
      const state =
        renderSections && this.cartItemsParent?._sendCartRequest
          ? await this.cartItemsParent._sendCartRequest(url, formData)
          : await fetchJson(url, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body:
                typeof formData !== "string"
                  ? JSON.stringify(formData)
                  : formData,
            });

      this.cartContent = state;
      return state;
    }

    async handleOptionChange(oldValue, newValue) {
      if (oldValue === newValue || oldValue === null || oldValue === "null") {
        return;
      }

      const lineIndex = this.getLineIndex();
      if (this.cartItemsParent && lineIndex) {
        this.cartItemsParent.enableLoading(lineIndex);
      }

      const label = this.animateInput?.closest(".addon-selected");
      const checkedInput = this.querySelector("input:checked");
      if (label) {
        label.querySelector(".price")?.classList.add("hidden");
        label
          .querySelector(".flowbite-loading-spinner")
          ?.classList.remove("hidden");
      }

      try {
        let item = null;
        if (oldValue !== "") {
          await this.fetchCartData();
          item = this.getCartItemByKey();
        } else {
          item = this.getParentItemFromDom();
          if (!item) {
            await this.fetchCartData();
            item = this.getCartItemByKey();
          }
        }

        const formData = {
          items: [],
        };

        if (newValue !== "") {
          formData.items.push({
            id: Number.parseInt(newValue, 10),
            quantity: item.quantity,
            properties: {
              type: "addon",
              _product_type: item.properties
                ? item.properties._product_type
                : "",
              _for_product: item.id.toString(),
              __covered_barcode: checkedInput?.dataset.barcode,
            },
          });
        }

        let renderedCartState = null;

        if (formData.items.length === 0) {
          if (oldValue !== "") {
            renderedCartState = await this.removeCartItem(oldValue, {
              renderSections: true,
            });
          }
        } else {
          if (oldValue !== "") {
            await this.removeCartItem(oldValue);
          }
          renderedCartState = await this.cartAction(formData, "cart/add.js", {
            renderSections: true,
          });
        }

        if (renderedCartState?.sections && this.cartItemsParent) {
          this.cartItemsParent._applyCartSnapshot(renderedCartState);
        }
      } finally {
        if (label) {
          label.querySelector(".price")?.classList.remove("hidden");
          label
            .querySelector(".flowbite-loading-spinner")
            ?.classList.add("hidden");
        }

        if (this.cartItemsParent && lineIndex) {
          this.cartItemsParent.disableLoading(lineIndex);
        }
      }
    }
  }

  customElements.define("cart-addon-selector", CartAddonSelector);
}
